import Alert from '@pelckmans/elementis/components/alert';
import { Cell } from '@pelckmans/elementis/components/table';
import HeaderCell from '@pelckmans/elementis/components/table/header-cell';
import Details from '@pelckmans/elementis/icons/details';
import { useTranslation } from 'react-i18next';
import { getResultPercentages } from '../../../../../utils/results';
import { getSessions } from '../../api/history';
import { ScorableItem } from '../../api/scorable-items';
import AbsoluteScoreCell from '../table/cells/AbsoluteScoreCell';
import DateCell from '../table/cells/DateCell';
import PercentageCell from '../table/cells/PercentageCell';
import TimeCell from '../table/cells/TimeCell';

const weekdayFormatter = new Intl.DateTimeFormat('nl-BE', {
  weekday: 'short',
});
const dayMonthFormatter = new Intl.DateTimeFormat('nl-BE', {
  day: '2-digit',
  month: '2-digit',
});

function FromToCell({ from, to }: { from: string; to: string }) {
  const { t } = useTranslation();
  const fromDate = new Date(from);
  const fromDateWeekDay = weekdayFormatter.format(fromDate);
  const fromDateDate = dayMonthFormatter.format(fromDate);

  const toDate = new Date(to);
  const toDateWeekDay = weekdayFormatter.format(toDate);
  const toDateDate = dayMonthFormatter.format(toDate);

  return (
    <Cell pillGroupProps={{ 'aria-label': undefined, role: 'group' }}>
      <Cell.Pill>
        {t(`${BASE_PATH}.TABLE_VALUES.FROM_TO`, {
          from: `${fromDateWeekDay} ${fromDateDate}`,
          to: `${toDateWeekDay} ${toDateDate}`,
        })}
      </Cell.Pill>
    </Cell>
  );
}

const BASE_PATH = 'PAGES.RESULTS.SESSION_HISTORY_MODAL';

export default function HistoryTable({
  sessionsData,
  scorableItem,
}: {
  sessionsData: Awaited<ReturnType<typeof getSessions>>;
  scorableItem: ScorableItem;
}) {
  const { t } = useTranslation();

  if (sessionsData.sessions.length === 0) {
    return (
      <Alert prominence="warning">
        {t(`${BASE_PATH}.TABLE_VALUES.NO_SESSIONS`, { memberName: sessionsData?.memberName })}
      </Alert>
    );
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <HeaderCell title={t(`${BASE_PATH}.TABLE_HEADER.TYPE`)} />
          <HeaderCell title={t(`${BASE_PATH}.TABLE_HEADER.AMOUNT_OF_ITEMS`)} />
          <HeaderCell title={t(`${BASE_PATH}.TABLE_HEADER.SHARED`)} />
          <HeaderCell title={t(`${BASE_PATH}.TABLE_HEADER.RESULTS`)} />
          <HeaderCell title={t(`${BASE_PATH}.TABLE_HEADER.PERCENTAGE`)} />
          <HeaderCell title={t(`${BASE_PATH}.TABLE_HEADER.SCORE`)} />
          <HeaderCell title={t(`${BASE_PATH}.TABLE_HEADER.TIME_SPENT`)} />
          <HeaderCell title={t(`${BASE_PATH}.TABLE_HEADER.DATE_SUBMITTED`)} />
          <HeaderCell />
        </tr>
      </thead>
      <tbody>
        {sessionsData?.sessions.map((session, i) => (
          <tr key={`${session.session}-${i}`}>
            <Cell>
              {session.assignment
                ? t(`${BASE_PATH}.TABLE_VALUES.ASSIGNMENT`)
                : t(`${BASE_PATH}.TABLE_VALUES.FREE_EXERCISE`)}
            </Cell>
            {scorableItem.unachievable ? <Cell /> : <Cell>{session.amountOfItems}</Cell>}
            {session.assignment !== undefined && session.assignment.from && session.assignment.to ? (
              <FromToCell from={session.assignment.from} to={session.assignment.to} />
            ) : (
              <Cell />
            )}
            <Cell>
              <Cell.Results
                {...getResultPercentages(session.score, session.lti?.gradingProgress === 'PendingManual')}
                quantifiable={!scorableItem.unachievable}
                status={session.status}
                showTooltip
                variant="full"
              />
            </Cell>
            <PercentageCell achieved={session.score.achieved} maximum={session.score.maximum} />
            <AbsoluteScoreCell scorableItem={scorableItem} score={session.score} />
            <TimeCell duration={session.duration} />
            <DateCell
              completedBySystem={session.completedBySystem ?? false}
              isZeroScore={session.isZeroScore ?? false}
              achievedAt={session.achievedAt}
            />
            <Cell
              buttonGroupProps={{
                'aria-label': t(`${BASE_PATH}.TABLE_VALUES.DETAILS`),
                role: 'group',
              }}
            >
              <Cell.Button
                startIcon={<Details />}
                prominence="neutral"
                aria-label={t(`${BASE_PATH}.TABLE_VALUES.DETAILS_BUTTON`)}
              />
            </Cell>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
